import { useEffect, useMemo } from 'react';
import styled from 'styled-components';
import ReactFlow, {
  Background,
  BackgroundVariant,
  Handle,
  MarkerType,
  Node as RFNode,
  Edge as RFEdge,
  NodeProps,
  Position,
  ReactFlowProvider,
  useNodesState,
  useReactFlow,
} from 'reactflow';
import 'reactflow/dist/base.css';
import { Tooltip } from '@mui/material';
import { colors } from '../../../style/theme';
import { ToolRendererArgs } from '../types';

type GraphNode = {
  id: string;
  label: string;
  type?: string;
  layer?: number;
};

type GraphEdge = {
  source: string;
  target: string;
};

const NODE_W = 130;
const NODE_H = 38;
const LAYER_GAP = 70;
const NODE_GAP = 20;
const PAD = 20;
const MAX_NODES = 10;
const MAX_LAYERS = 5;

const buildLayout = (
  nodes: GraphNode[],
): Record<string, { x: number; y: number }> => {
  const layerMap = new Map<number, GraphNode[]>();
  nodes.forEach((n) => {
    const l = n.layer ?? 0;
    if (!layerMap.has(l)) layerMap.set(l, []);
    layerMap.get(l)!.push(n);
  });

  const sortedLayers = Array.from(layerMap.keys()).sort((a, b) => a - b);
  const maxPerLayer = Math.max(
    ...Array.from(layerMap.values()).map((g) => g.length),
    1,
  );
  const totalH =
    PAD * 2 + maxPerLayer * NODE_H + Math.max(0, maxPerLayer - 1) * NODE_GAP;

  const pos: Record<string, { x: number; y: number }> = {};
  sortedLayers.forEach((layerKey, colIdx) => {
    const group = layerMap.get(layerKey)!;
    const groupH =
      group.length * NODE_H + Math.max(0, group.length - 1) * NODE_GAP;
    const startY = (totalH - groupH) / 2;
    group.forEach((node, rowIdx) => {
      pos[node.id] = {
        x: PAD + colIdx * (NODE_W + LAYER_GAP),
        y: startY + rowIdx * (NODE_H + NODE_GAP),
      };
    });
  });

  return pos;
};

type NodeData = { label: string; fullLabel: string };

const NodeBox = styled.div`
  width: ${NODE_W}px;
  height: ${NODE_H}px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${({ theme }) => theme.palette.common.white};
  border: 1.5px solid ${({ theme }) => theme.palette.common.gray17};
  border-radius: 5px;
  font-size: 11px;
  font-family: monospace;
  font-weight: 600;
  color: ${({ theme }) => theme.palette.common.black};
  box-sizing: border-box;
  padding: 0 8px;
  cursor: grab;
`;

const handleStyle = {
  background: colors.gray7,
  border: 'none',
  width: 6,
  height: 6,
};

const FlowContainer = styled.div`
  margin-top: 8px;
  height: 340px;
  border: 1px solid ${({ theme }) => theme.palette.divider};
  border-radius: 4px;
  overflow: hidden;

  .react-flow__attribution {
    display: none;
  }

  .react-flow__handle {
    pointer-events: none;
  }
`;

const StyledReactFlow = styled(ReactFlow)`
  width: 100%;
  height: 100%;
`;

const StyledBackground = styled(Background)`
  background-color: ${({ theme }) => theme.palette.common.gray3};
`;

// Must be defined at module level to avoid ReactFlow re-mounting nodes
const LineageNode = ({ data }: NodeProps<NodeData>) => (
  <>
    <Handle type="target" position={Position.Left} style={handleStyle} />
    <Tooltip
      title={data.label !== data.fullLabel ? data.fullLabel : ''}
      placement="top"
      arrow
    >
      <NodeBox>{data.label}</NodeBox>
    </Tooltip>
    <Handle type="source" position={Position.Right} style={handleStyle} />
  </>
);

const nodeTypes = { lineage: LineageNode };

type LineageGraphProps = { nodes: GraphNode[]; edges: GraphEdge[] };

const LineageGraphInner = ({ nodes, edges }: LineageGraphProps) => {
  // Enforce limits
  const slicedNodes = nodes.slice(0, MAX_NODES);
  const uniqueLayers = [...new Set(slicedNodes.map((n) => n.layer ?? 0))]
    .sort((a, b) => a - b)
    .slice(0, MAX_LAYERS);
  const allowedLayers = new Set(uniqueLayers);
  const safeNodes = slicedNodes.filter((n) => allowedLayers.has(n.layer ?? 0));
  const nodeSet = new Set(safeNodes.map((n) => n.id));
  const safeEdges = edges.filter(
    (e) => nodeSet.has(e.source) && nodeSet.has(e.target),
  );

  const initialNodes: RFNode<NodeData>[] = useMemo(() => {
    const pos = buildLayout(safeNodes);
    return safeNodes.map((node) => ({
      id: node.id,
      type: 'lineage',
      position: pos[node.id] ?? { x: 0, y: 0 },
      data: {
        label:
          node.label.length > 17
            ? `${node.label.slice(0, 16)}\u2026`
            : node.label,
        fullLabel: node.label,
      },
      style: { width: NODE_W, height: NODE_H },
    }));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const rfEdges: RFEdge[] = useMemo(
    () =>
      safeEdges.map((edge, i) => ({
        id: `e-${i}`,
        source: edge.source,
        target: edge.target,
        style: { stroke: colors.gray7, strokeWidth: 1.5 },
        markerEnd: {
          type: MarkerType.ArrowClosed,
          color: colors.gray7,
          width: 14,
          height: 10,
        },
      })),
    [], // eslint-disable-line react-hooks/exhaustive-deps
  );

  const [rfNodes, , onNodesChange] = useNodesState(initialNodes);
  const { fitView } = useReactFlow();

  useEffect(() => {
    fitView({ padding: 0.15 });
  }, [fitView]);

  if (safeNodes.length === 0) return null;

  return (
    <FlowContainer>
      <StyledReactFlow
        nodes={rfNodes}
        edges={rfEdges}
        nodeTypes={nodeTypes}
        onNodesChange={onNodesChange}
        fitView
        fitViewOptions={{ padding: 0.15 }}
        nodesDraggable
        nodesConnectable={false}
        elementsSelectable={false}
        panOnDrag
        zoomOnScroll
        zoomOnPinch
      >
        <StyledBackground
          variant={BackgroundVariant.Dots}
          gap={25}
          size={3}
          color={colors.gray7}
        />
      </StyledReactFlow>
    </FlowContainer>
  );
};

const LineageGraph = (props: LineageGraphProps) => (
  <ReactFlowProvider>
    <LineageGraphInner {...props} />
  </ReactFlowProvider>
);

const lineageGraphToolRenderer = (toolArgs: ToolRendererArgs) => (
  <LineageGraph nodes={toolArgs?.nodes ?? []} edges={toolArgs?.edges ?? []} />
);

export default lineageGraphToolRenderer;
